import type { PackageInclusionRecord as OfferPackageInclusionRecord } from '~/hooks/apis/inclusions/useGetPackageInclusionsByOffer';

import { getPackageName, getPackagePersistId } from '../OfferPackages/utils';
import {
  type PackageInclusionRecord,
  getInclusionCategory,
  getInclusionName,
  getInclusionPersistId,
  isInclusionHidden,
} from '../utils';

export type { PackageInclusionRecord };

export interface MasterInclusion {
  id: string;
  assignedPackageIds: Array<string>;
  category: string;
  highlighted: boolean;
  inclusion: string;
  luxPlusOnly: boolean;
  persistId?: string;
}

export interface PackageOption {
  id: string;
  name: string;
}

export const TABLE_GRID_COLUMNS = '40px minmax(150px, 180px) minmax(260px, 1fr) 110px 130px minmax(200px, 240px) 40px';

type OfferPackageInclusionWithDisplayFields = OfferPackageInclusionRecord &
  PackageInclusionRecord & {
    inclusion: OfferPackageInclusionRecord['inclusion'] & NonNullable<PackageInclusionRecord['inclusion']>;
  };

export function getPackageOptions(packages?: Array<App.Package>): Array<PackageOption> {
  return (
    packages?.map((offerPackage, index) => ({
      id: getPackagePersistId(offerPackage) ?? `package-${index}`,
      name: getPackageName(offerPackage),
    })) ?? []
  );
}

function getInclusionKey(inclusion: OfferPackageInclusionWithDisplayFields) {
  return getInclusionName(inclusion).trim().toLowerCase();
}

export function buildMasterInclusionsFromOffer(
  packageInclusions: Record<string, Array<OfferPackageInclusionRecord>> | undefined,
): Array<MasterInclusion> {
  if (!packageInclusions) {
    return [];
  }

  const inclusionsById = new Map<string, MasterInclusion>();

  Object.entries(packageInclusions).forEach(([packageId, inclusions]) => {
    inclusions.forEach((rawInclusion) => {
      // Package inclusion payloads can include extra display fields; missing values intentionally fall back to off.
      const inclusion = rawInclusion as OfferPackageInclusionWithDisplayFields;
      const inclusionName = getInclusionName(inclusion);

      if (!inclusionName || isInclusionHidden(inclusion)) {
        return;
      }

      const inclusionKey = getInclusionKey(inclusion);
      const existingInclusion = inclusionsById.get(inclusionKey);

      if (existingInclusion) {
        if (!existingInclusion.assignedPackageIds.includes(packageId)) {
          existingInclusion.assignedPackageIds.push(packageId);
        }

        return;
      }

      inclusionsById.set(inclusionKey, {
        id: inclusionKey,
        assignedPackageIds: [packageId],
        category: getInclusionCategory(inclusion),
        highlighted: inclusion.style === 'highlighted',
        inclusion: inclusionName,
        luxPlusOnly: !!inclusion.lux_plus_tier,
        persistId: getInclusionPersistId(inclusion),
      });
    });
  });

  return Array.from(inclusionsById.values());
}

export function buildPackageInclusionsFromMaster(
  masterInclusions: Array<MasterInclusion>,
  packageOptions: Array<PackageOption>,
): Record<string, Array<PackageInclusionRecord>> {
  return packageOptions.reduce<Record<string, Array<PackageInclusionRecord>>>((packageInclusions, packageOption) => {
    const assignedInclusions = masterInclusions
      .filter((inclusion) => inclusion.inclusion.trim() && inclusion.assignedPackageIds.includes(packageOption.id))
      .map((inclusion) => ({
        isUnique: inclusion.assignedPackageIds.length === 1,
        packageInclusion: {
          ...(inclusion.persistId ? { inclusion_id: inclusion.persistId } : { id: inclusion.id }),
          inclusion: {
            ...(inclusion.persistId ? { id: inclusion.persistId } : {}),
            name: inclusion.inclusion,
            is_hidden: false,
            category: {
              name: inclusion.category,
            },
          },
          lux_plus_tier: inclusion.luxPlusOnly ? 'base' : null,
          style: inclusion.highlighted ? 'highlighted' : null,
        },
      }));

    packageInclusions[packageOption.id] = assignedInclusions
      .filter((inclusion) => inclusion.isUnique)
      .concat(assignedInclusions.filter((inclusion) => !inclusion.isUnique))
      .map(({ packageInclusion }) => packageInclusion);

    return packageInclusions;
  }, {});
}

export function getPackageInclusionIdsToUnassign(
  masterInclusions: Array<MasterInclusion>,
  packageInclusions: Record<string, Array<OfferPackageInclusionRecord>> | undefined,
): Array<string> {
  if (!packageInclusions) {
    return [];
  }

  const assignedPackageIdsByInclusionId = new Map(
    masterInclusions.flatMap((inclusion) =>
      inclusion.persistId ? [[inclusion.persistId, new Set(inclusion.assignedPackageIds)] as const] : [],
    ),
  );

  return Object.entries(packageInclusions).flatMap(([packageId, inclusions]) =>
    inclusions.flatMap((rawInclusion) => {
      const inclusion = rawInclusion as OfferPackageInclusionWithDisplayFields;
      const inclusionId = getInclusionPersistId(inclusion);
      const rowId = rawInclusion.id ? String(rawInclusion.id) : undefined;
      const assignedPackageIds = inclusionId ? assignedPackageIdsByInclusionId.get(inclusionId) : undefined;

      if (!inclusionId || !rowId || !assignedPackageIds || isInclusionHidden(inclusion)) {
        return [];
      }

      return assignedPackageIds.has(packageId) ? [] : [rowId];
    }),
  );
}
